import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { format } from "date-fns";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import PageBreadcrumbs from "../components/PageBreadcrumbs";
import { useResults } from "../hooks/useResults";
import { useUploadHistory } from "../hooks/useUploadHistory";

function UploadHistoryPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [uploads, setUploads] = useState([]);
  const [downloadUrls, setDownloadUrls] = useState({});

  const compYearUSEFCompID = searchParams.get("compYearUSEFCompID");

  const { compYear: routeCompYear, compid: routeCompid } = useParams();
  const compYear = routeCompYear || (compYearUSEFCompID ? compYearUSEFCompID.split("#")[0] : null);
  const compid = routeCompid || (compYearUSEFCompID ? compYearUSEFCompID.split("#")[1] : null);

  // Fetch comps to get show name
  const { comps } = useResults();

  // Find the comp
  const comp = comps.find((c) => c.compId === compid);
  const compName = comp?.showName || compid;

  const { fetchUploadHistory, getDownloadUrl, loading, error } = useUploadHistory();

  useEffect(() => {
    if (!compYearUSEFCompID) {
      return;
    }

    const fetchUploads = async () => {
      try {
        const data = await fetchUploadHistory(compYearUSEFCompID);
        setUploads(data);
      } catch (err) {
        console.error("Error fetching upload history:", err);
        // Error is handled by the hook
      }
    };

    fetchUploads();
  }, [compYearUSEFCompID, fetchUploadHistory]);

  const handleDownload = async (s3Path) => {
    // Check if we already have a URL for this path
    if (downloadUrls[s3Path]) {
      window.open(downloadUrls[s3Path], "_blank");
      return;
    }

    try {
      const url = await getDownloadUrl(s3Path);
      setDownloadUrls((prev) => ({ ...prev, [s3Path]: url }));
      window.open(url, "_blank");
    } catch (err) {
      console.error("Error generating download URL:", err);
      // Error is handled by the hook
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MM/dd/yyyy HH:mm:ss");
    } catch (err) {
      console.error("Error formatting date:", err);
      return dateString;
    }
  };


  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <PageBreadcrumbs
            items={[
              { label: t("results.title"), href: "/results" },
              ...(compid && compYear
                ? [{ label: `${compName} ${compYear}`, href: `/results/${compYear}/${compid}` }]
                : compid
                  ? [{ label: compName, href: `/results/${compid}` }]
                  : []),
              { label: t("results.uploadHistory.title") },
            ]}
          />
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            if (compid && compYear) {
              navigate(`/results/${compYear}/${compid}`);
            } else if (compid) {
              navigate(`/results/${compid}`);
            } else {
              navigate("/results");
            }
          }}
        >
          {t("results.uploadHistory.back")}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t("results.uploadHistory.title")}
        </Typography>
        {compName && compYear && (
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            {compName}
          </Typography>
        )}
        {compYearUSEFCompID && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {compYear && (
              <>
                {t("results.upload.preview.compYear")}: {compYear} •{" "}
              </>
            )}
            {t("results.upload.preview.usefCompId")}: {compid}
          </Typography>
        )}

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : uploads.length === 0 ? (
          <Alert severity="info">{t("results.uploadHistory.empty")}</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("results.uploadHistory.columns.date")}</TableCell>
                  <TableCell>{t("results.uploadHistory.columns.checksum")}</TableCell>
                  <TableCell>{t("results.uploadHistory.columns.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploads.map((upload) => (
                  <TableRow key={upload.id} hover>
                    <TableCell>{formatDate(upload.createdAt)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.875rem",
                          wordBreak: "break-all",
                        }}
                      >
                        {upload.checksum || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(upload.s3Path)}
                      >
                        {t("results.uploadHistory.download")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default UploadHistoryPage;
